<template>
  <div>
    <KTCodePreview v-bind:title="pageTitle" class="cat-target-detail">
      <template v-slot:toolbar>
        <div class="d-flex flex-wrap">
          <b-button
            v-if="canUpdate"
            size="sm"
            variant="primary"
            class="ml-3 mb-1 font-weight-bolder"
            @click="$refs.addMembersModal.show()"
          >
            <i style="font-size: 1rem" class="flaticon2-add-1"></i>Thêm sản phẩm
          </b-button>
        </div>
      </template>
      <template v-slot:preview>
        <b-row class="mb-3">
          <b-col cols="12" sm="6" md="4" lg="3" class="mb-2">
            <label class="font-weight-bold mb-1">Xem sản phẩm tại ngày</label>
            <date-picker
              class="form-control form-control-sm"
              :config="dpAsOfDate"
              v-model="filter.asOfDate"
            ></date-picker>
          </b-col>
          <b-col cols="12" sm="6" md="5" lg="4" class="mb-2">
            <label class="font-weight-bold mb-1">Tìm sản phẩm</label>
            <b-input
              placeholder="Tìm sản phẩm"
              v-model="filter.search"
              class="btn-sm"
              v-on:keyup.enter="onFilter()"
            ></b-input>
          </b-col>
          <b-col cols="auto" class="mb-2 d-flex align-items-end">
            <b-button
              style="fontweight: 600; width: 70px"
              variant="primary"
              size="sm"
              :disabled="onLoading"
              @click="onFilter()"
              >Lọc</b-button
            >
          </b-col>
        </b-row>

        <p class="text-dark mb-3" style="font-weight: 500">
          {{ totalItems }} sản phẩm thuộc danh mục chỉ tiêu này tại ngày
          {{ appliedDateText }}.
        </p>

        <b-table
          :items="listData"
          :fields="fields"
          bordered
          hover
          responsive
          show-empty
          :busy="onLoading"
          :empty-text="`Chưa có sản phẩm nào thuộc danh mục chỉ tiêu này tại ngày ${appliedDateText}.`"
        >
          <template v-slot:table-busy>
            <vcl-table :speed="5" :animate="true" :columns="6"></vcl-table>
          </template>
          <template v-slot:cell(productCode)="row">
            <span class="text-break">{{ row.item.productCode }}</span>
          </template>
          <template v-slot:cell(productName)="row">
            <p class="name-clamp mb-0 text-break" :title="row.item.productName">
              {{ row.item.productName }}
            </p>
          </template>
          <template v-slot:cell(effectiveFrom)="row">
            {{ toClientDate(row.item.effectiveFrom) }}
          </template>
          <template v-slot:cell(status)="row">
            <span
              :class="[
                'label font-weight-bold label-lg label-inline',
                row.item.status === STATUS_PENDING
                  ? 'label-light-warning'
                  : 'label-light-success',
              ]"
              style="width: max-content"
              >{{ statusName(row.item.status) }}</span
            >
          </template>
          <template v-slot:cell(source)="row">
            {{ sourceName(row.item.source) }}
            <!-- Người dùng chốt 05/10/2026 (ngoài SRS 10.2): tên - mã nhân viên người khai/duyệt. -->
            <div v-if="personLabel(row.item)" class="text-muted font-size-sm text-break">
              {{ personLabel(row.item) }}
            </div>
          </template>
          <template v-slot:cell(actions)="row">
            <b-button
              size="sm"
              variant="light-primary"
              class="text-nowrap"
              @click="openHistory(row.item)"
              >Lịch sử</b-button
            >
          </template>
        </b-table>

        <b-row>
          <b-col cols="12" md="4">
            <p class="mt-3 text-dark" style="font-weight: 500">
              Tổng số: {{ totalItems }}
            </p>
          </b-col>
          <b-col cols="12" md="8">
            <b-pagination-nav
              class="custom-pagination"
              v-show="totalPages >= 2"
              :disabled="onLoading"
              :link-gen="linkGen"
              :number-of-pages="totalPages"
              use-router
              align="right"
              first-class="page-item-first btn btn-icon btn-sm mr-1 my-1"
              prev-class="page-item-prev btn btn-icon btn-sm mr-.5 my-1"
              next-class="page-item-next btn btn-icon btn-sm mr-1 my-1 ml-.5"
              last-class="page-item-last btn btn-icon btn-sm my-1 "
              page-class="btn btn-icon btn-sm border-0 mr-.5 my-1"
            >
              <template v-slot:first-text>
                <span><i class="ki ki-bold-double-arrow-back icon-xs"></i></span>
              </template>
              <template v-slot:prev-text>
                <i class="ki ki-bold-arrow-back icon-xs"></i>
              </template>
              <template v-slot:next-text>
                <i class="ki ki-bold-arrow-next icon-xs"></i>
              </template>
              <template v-slot:last-text>
                <span class="text-info">
                  <i class="ki ki-bold-double-arrow-next icon-xs"></i>
                </span>
              </template>
            </b-pagination-nav>
          </b-col>
        </b-row>

        <!-- Hộp "Danh mục chỉ tiêu theo thời gian" (SRS 10.2, FR-210) -->
        <b-modal
          ref="historyModal"
          :title="`Danh mục chỉ tiêu theo thời gian — ${history.productName}`"
          size="lg"
          hide-footer
        >
          <b-table
            :items="history.rows"
            :fields="historyFields"
            bordered
            responsive
            :busy="history.loading"
          >
            <template v-slot:table-busy>
              <vcl-table :speed="5" :animate="true" :columns="5"></vcl-table>
            </template>
            <template v-slot:cell(effectiveFrom)="row">
              {{ toClientDate(row.item.effectiveFrom) }}
            </template>
            <template v-slot:cell(status)="row">
              {{ statusName(row.item.status) }}
            </template>
            <template v-slot:cell(person)="row">
              {{ personLabel(row.item) || '—' }}
            </template>
            <template v-slot:cell(reason)="row">
              <span class="text-break">{{ row.item.reason || '—' }}</span>
            </template>
          </b-table>
          <p class="text-muted mb-0">
            Người sửa và giá trị trước, sau xem ở tab Lịch sử của màn sửa sản
            phẩm.
          </p>
        </b-modal>

        <AddMembersModal
          v-if="canUpdate"
          ref="addMembersModal"
          :cat-target-id="catTargetId"
          @saved="fetchMembers"
        />
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import { VclTable } from 'vue-content-loading';
import AddMembersModal from '@/view/components/cat-target/AddMembersModal.vue';
import localData from '@/utils/saveDataToLocal';
import { makeToastFaile, getApiErrorMessage } from '@/utils/common';
import {
  DP_CONFIG,
  DATE_FORMAT,
  YEAR_MONTH_DAY_FORMAT,
  formatSpecificDate,
  getCurrentDate,
} from '@/utils/date';
import {
  CAT_TARGET_MEMBER_STATUS_NAME,
  CAT_TARGET_MEMBER_SOURCE_NAME,
} from '@/utils/constants';

// t102962 — Màn 2 Chi tiết danh mục chỉ tiêu (SRS 10.2, FR-106, FR-209, FR-210); nút "Thêm sản
// phẩm" mở hộp AddMembersModal (P5a), chỉ hiện khi có CAT_TARGET_UPDATE (FR-602).
const API = 'v2/cat-targets';
const PAGE_SIZE = 50;
const STATUS_PENDING = 1;
const SOURCE_PROPOSAL = 3; // máy đề xuất: không có người khai, hiện người duyệt khi đã duyệt

const thStyle = (width, minWidth) => ({ fontWeight: 600, color: '#181c32', width, minWidth });

export default {
  components: { KTCodePreview, VclTable, AddMembersModal },
  data() {
    return {
      STATUS_PENDING,
      catTarget: null,
      listData: [],
      onLoading: false,
      membersSeq: 0,
      totalPages: 1,
      totalItems: 0,
      // Ô đang nhập và bộ lọc đã áp dụng tách riêng: phân trang dùng bộ lọc đã bấm Lọc.
      filter: { asOfDate: getCurrentDate(DATE_FORMAT), search: '' },
      applied: { asOfDate: getCurrentDate(DATE_FORMAT), search: '' },
      dpAsOfDate: { ...DP_CONFIG.date, showClear: false },
      history: { productName: '', rows: [], loading: false, requestSeq: 0 },
      fields: [
        { key: 'productCode', label: 'Mã', thStyle: thStyle('18%', '110px'), tdClass: 'align-middle' },
        {
          key: 'productName',
          label: 'Tên sản phẩm',
          thStyle: thStyle('37%', '200px'),
          tdClass: 'align-middle',
        },
        {
          key: 'effectiveFrom',
          label: 'Hiệu lực từ',
          thStyle: thStyle('12%', '100px'),
          thClass: 'text-center',
          tdClass: 'text-center align-middle',
        },
        {
          key: 'status',
          label: 'Trạng thái',
          thStyle: thStyle('11%', '100px'),
          thClass: 'text-center',
          tdClass: 'text-center align-middle',
        },
        { key: 'source', label: 'Nguồn', thStyle: thStyle('14%', '130px'), tdClass: 'align-middle' },
        {
          key: 'actions',
          label: '',
          thStyle: thStyle('8%', '80px'),
          tdClass: 'text-center align-middle',
        },
      ],
      historyFields: [
        {
          key: 'effectiveFrom',
          label: 'Hiệu lực từ',
          thStyle: thStyle('20%', '100px'),
          tdClass: 'align-middle',
        },
        {
          key: 'catTargetCode',
          label: 'Danh mục chỉ tiêu',
          thStyle: thStyle('25%', '120px'),
          tdClass: 'align-middle text-break',
        },
        {
          key: 'status',
          label: 'Trạng thái',
          thStyle: thStyle('13%', '90px'),
          tdClass: 'align-middle',
        },
        // Người dùng chốt 05/10/2026 (ngoài SRS 10.2).
        {
          key: 'person',
          label: 'Người khai',
          thStyle: thStyle('22%', '140px'),
          tdClass: 'align-middle text-break',
        },
        { key: 'reason', label: 'Lý do', thStyle: thStyle('25%', '160px'), tdClass: 'align-middle' },
      ],
    };
  },
  computed: {
    catTargetId() {
      return this.$route.query.id;
    },
    canUpdate() {
      return localData.checkPermission('CAT_TARGET_UPDATE');
    },
    pageTitle() {
      return this.catTarget
        ? `${this.catTarget.code} — ${this.catTarget.name}`
        : 'Chi tiết danh mục chỉ tiêu';
    },
    appliedDateText() {
      return this.applied.asOfDate;
    },
  },
  watch: {
    // Phân trang (use-router) và Back/Forward đổi `?page` → tải theo URL.
    '$route.query.page'() {
      this.fetchMembers();
    },
    // Đổi sang danh mục khác ngay trên màn này (cùng route, khác `?id`) → tải lại cả tiêu đề lẫn bảng.
    '$route.query.id'() {
      this.loadDetail();
    },
  },
  created() {
    this.loadDetail();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Sản phẩm' },
      { title: 'Danh mục chỉ tiêu', route: '/cat-targets' },
      { title: 'Chi tiết' },
    ]);
  },
  methods: {
    toClientDate(date) {
      return formatSpecificDate(date, YEAR_MONTH_DAY_FORMAT, DATE_FORMAT);
    },
    statusName(status) {
      return CAT_TARGET_MEMBER_STATUS_NAME[status] || '';
    },
    sourceName(source) {
      return CAT_TARGET_MEMBER_SOURCE_NAME[source] || '';
    },
    // "Tên - Mã nhân viên"; máy đề xuất → "Duyệt: …" khi đã duyệt, đang chờ thì trống.
    personLabel(item) {
      const join = (name, code) => (name ? [name, code].filter(Boolean).join(' - ') : '');
      if (item.source === SOURCE_PROPOSAL) {
        const approver = join(item.approvedByName, item.approvedByCode);
        return item.status !== STATUS_PENDING && approver ? `Duyệt: ${approver}` : '';
      }
      return join(item.createdByName, item.createdByCode);
    },
    currentPage() {
      return Number(this.$route.query.page) || 1;
    },
    // Vào màn không có `?id` (gõ tay URL) → về danh sách, không gọi API `cat-targets/undefined`.
    loadDetail() {
      if (!this.catTargetId) {
        this.$router.replace({ name: 'list-cat-target' }).catch(() => {});
        return;
      }
      this.catTarget = null;
      this.fetchCatTarget();
      this.fetchMembers();
    },
    fetchCatTarget() {
      const id = this.catTargetId;
      ApiService.query(`${API}/${id}`)
        .then(({ data }) => {
          // Đã chuyển sang danh mục khác trong lúc chờ → bỏ kết quả cũ.
          if (id === this.catTargetId) {
            this.catTarget = data.data;
          }
        })
        .catch((err) => {
          makeToastFaile(getApiErrorMessage(err));
        });
    },
    fetchMembers() {
      // Back/Forward bấm nhanh gọi chồng request: chỉ nhận kết quả của lần gọi mới nhất
      // (rule 12 mục 9.1), để trang cũ về muộn không đè trang đang hiện.
      const seq = ++this.membersSeq;
      this.onLoading = true;
      const params = {
        asOfDate: formatSpecificDate(this.applied.asOfDate, DATE_FORMAT, YEAR_MONTH_DAY_FORMAT),
        search: this.applied.search,
        page: this.currentPage(),
        limit: PAGE_SIZE,
      };
      ApiService.query(`${API}/${this.catTargetId}/members`, { params })
        .then(({ data }) => {
          if (seq !== this.membersSeq) {
            return;
          }
          this.totalPages = data.data.totalPage;
          this.totalItems = data.data.totalRow;
          this.listData = data.data.dataset;
        })
        .catch((err) => {
          if (seq === this.membersSeq) {
            makeToastFaile(getApiErrorMessage(err));
          }
        })
        .finally(() => {
          if (seq === this.membersSeq) {
            this.onLoading = false;
          }
        });
    },
    // Chỉ gọi API khi Enter/bấm Lọc; đang tải thì bỏ qua (rule 12 mục 9.1).
    onFilter() {
      if (this.onLoading) {
        return;
      }
      this.applied = {
        asOfDate: this.filter.asOfDate || getCurrentDate(DATE_FORMAT),
        search: this.filter.search,
      };
      if (this.currentPage() === 1) {
        this.fetchMembers();
        return;
      }
      this.$router
        .push({ name: 'detail-cat-target', query: { id: this.catTargetId } })
        .catch(() => {});
    },
    linkGen(pageNum) {
      const query = { id: this.catTargetId };
      if (pageNum > 1) {
        query.page = pageNum;
      }
      return { name: 'detail-cat-target', query };
    },
    openHistory(item) {
      // Bấm "Lịch sử" liên tiếp nhiều dòng: chỉ nhận kết quả của lần mở mới nhất.
      const seq = this.history.requestSeq + 1;
      this.history = { productName: item.productName, rows: [], loading: true, requestSeq: seq };
      this.$refs.historyModal.show();
      ApiService.query(`${API}/members/history/${item.productId}`)
        .then(({ data }) => {
          if (this.history.requestSeq === seq) {
            this.history.rows = data.data;
          }
        })
        .catch((err) => {
          if (this.history.requestSeq === seq) {
            makeToastFaile(getApiErrorMessage(err));
          }
        })
        .finally(() => {
          if (this.history.requestSeq === seq) {
            this.history.loading = false;
          }
        });
    },
  },
};
</script>

<style scoped>
.name-clamp {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
/* Mã 50 ký tự + tên dài: tiêu đề xuống dòng trong phần của nó, nút "Thêm sản phẩm" giữ bên phải
   (khung chung đặt toolbar `col-md-6` + căn giữa nên bị đẩy xuống giữa dòng dưới). */
.cat-target-detail ::v-deep .card-header > .card-title {
  flex: 1 1 0;
  min-width: 0;
}
.cat-target-detail ::v-deep .card-header > .card-title .card-label {
  overflow-wrap: anywhere;
}
.cat-target-detail ::v-deep .card-header > .card-toolbar {
  flex: 0 0 auto;
  width: auto;
  max-width: none;
}
.cat-target-detail ::v-deep .card-header .example-tools {
  justify-content: flex-end !important;
}
</style>
